import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const EXPECTED_COLUMNS = [
  'property_address', 'property_name', 'city', 'state', 'zip',
  'unit_number', 'email', 'first_name', 'last_name', 'phone', 'landlord_email',
  'lease_type', 'lease_start', 'lease_end', 'last_renewal',
];

const OPT_IN_CATEGORIES = ['section_8', 'scrie', 'drie', 'rent_controlled'];
const LEASE_LABELS = {
  free_market: 'Free Market', rent_stabilized: 'Rent Stabilized', rent_controlled: 'Rent Controlled',
  section_8: 'Section 8', scrie: 'SCRIE', drie: 'DRIE', employee: 'Employee', sublease: 'Sublease', other: 'Other',
};

function classifyLeaseClient(leaseType) {
  if (!leaseType) return 'free_market';
  const lt = leaseType.toLowerCase().trim();
  if (/section\s*8|housing\s*choice|hcv/i.test(lt)) return 'section_8';
  if (/scrie|senior\s*citizen\s*rent/i.test(lt)) return 'scrie';
  if (/drie|disability\s*rent/i.test(lt)) return 'drie';
  if (/rent.?controlled|rc\b/i.test(lt)) return 'rent_controlled';
  if (/rent.?stab|rs\b/i.test(lt)) return 'rent_stabilized';
  if (/employee|staff|super/i.test(lt)) return 'employee';
  if (/sub.?le/i.test(lt)) return 'sublease';
  if (/free.?market|market.?rate|fm\b/i.test(lt)) return 'free_market';
  return 'other';
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(line => {
    const values = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = values[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

export default function BulkImport() {
  const { role } = useAuth();
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);       // { headers, rows }
  const [migrationDone, setMigrationDone] = useState(false);
  const [columnMap, setColumnMap] = useState({});    // csvHeader -> expectedColumn
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [liveConfirmed, setLiveConfirmed] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const { headers, rows } = parseCSV(evt.target.result);
      if (!headers.length) { toast.error('Could not parse CSV'); return; }
      setParsed({ headers, rows });

      // Auto-map columns by matching header names
      const map = {};
      headers.forEach(h => {
        const normalized = h.toLowerCase().trim().replace(/\s+/g, '_');
        const match = EXPECTED_COLUMNS.find(ec =>
          ec === normalized ||
          normalized.includes(ec) ||
          ec.includes(normalized) ||
          (ec === 'unit_number' && (normalized === 'apartment_number' || normalized === 'unit' || normalized === 'apt')) ||
          (ec === 'first_name' && (normalized === 'tenant_first_name' || normalized === 'firstname')) ||
          (ec === 'last_name' && (normalized === 'tenant_last_name' || normalized === 'lastname')) ||
          (ec === 'property_address' && (normalized === 'address' || normalized === 'building_address')) ||
          (ec === 'lease_type' && (normalized === 'lease' || normalized === 'lease_type' || normalized === 'leasetype')) ||
          (ec === 'lease_start' && (normalized === 'lease_start' || normalized === 'lease_start_date' || normalized === 'move_in')) ||
          (ec === 'lease_end' && (normalized === 'lease_end' || normalized === 'lease_end_date' || normalized === 'lease_expiry' || normalized === 'move_out')) ||
          (ec === 'last_renewal' && (normalized === 'last_renewal' || normalized === 'renewal_date'))
        );
        if (match) map[h] = match;
      });
      setColumnMap(map);
    };
    reader.readAsText(f);
  };

  const handleMapChange = (csvHeader, value) => {
    setColumnMap(prev => {
      const next = { ...prev };
      if (value) next[csvHeader] = value;
      else delete next[csvHeader];
      return next;
    });
  };

  const buildMappedRows = () => {
    if (!parsed) return [];
    return parsed.rows.map(row => {
      const mapped = {};
      Object.entries(columnMap).forEach(([csvH, targetCol]) => {
        mapped[targetCol] = row[csvH] || '';
      });
      return mapped;
    });
  };

  const handleDryRun = async () => {
    const mappedRows = buildMappedRows();
    if (!mappedRows.some(r => r.property_address)) {
      toast.error('No rows have a property_address mapped. Please map columns.');
      return;
    }
    setImporting(true);
    setLiveConfirmed(false);
    try {
      const res = await api.post('/admin/bulk-import', { rows: mappedRows, dry_run: true });
      setResults(res.data);
      toast.success('Dry run complete — no records were created');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Dry run failed');
    } finally {
      setImporting(false);
    }
  };

  const handleLiveImport = async () => {
    if (!liveConfirmed) return;
    const mappedRows = buildMappedRows();
    setImporting(true);
    try {
      const res = await api.post('/admin/bulk-import', { rows: mappedRows, dry_run: false });
      setResults(res.data);
      if (res.data.dry_run) {
        toast('Server is in dry-run mode (IMPORT_MODE is not set to "live")', { icon: 'ℹ️' });
      } else {
        toast.success(`Live import complete: ${res.data.tenants_created} tenants created`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setParsed(null);
    setColumnMap({});
    setResults(null);
    setLiveConfirmed(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const previewRows = parsed ? parsed.rows.slice(0, 10) : [];
  const mappedCount = Object.keys(columnMap).length;
  const requiredMapped = ['property_address', 'unit_number'].every(r =>
    Object.values(columnMap).includes(r)
  );

  // Lease category analysis
  const leaseColHeader = Object.entries(columnMap).find(([, v]) => v === 'lease_type')?.[0];
  const categoryCounts = {};
  let optInCount = 0;
  if (parsed && leaseColHeader) {
    parsed.rows.forEach(row => {
      const cat = classifyLeaseClient(row[leaseColHeader]);
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
      if (OPT_IN_CATEGORIES.includes(cat)) optInCount++;
    });
  }

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Bulk Import</div>
          <div className="page-sub">Import properties and tenants from a CSV file.</div>

          {role === 'super_admin' && !migrationDone && (
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#0369a1', fontSize: 13 }}>Database migration available</div>
                <div style={{ fontSize: 12, color: '#0369a1' }}>Adds lease_type, lease_category, voucher_type, billing_eligible, compliance_notes columns to tenants.</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                try {
                  const res = await api.post('/admin/bulk-import/migrate');
                  toast.success(res.data.message || 'Migration complete');
                  setMigrationDone(true);
                } catch (err) { toast.error(err.response?.data?.error || 'Migration failed'); }
              }}>Run Migration</button>
            </div>
          )}

          {/* Upload area */}
          {!parsed && !results && (
            <div className="card">
              <div className="card-header"><div className="card-title">Upload CSV</div></div>
              <div className="card-body">
                <div style={{
                  border: '2px dashed var(--border)', borderRadius: 12, padding: 40,
                  textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s',
                }}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = 'var(--border)';
                    const dt = e.dataTransfer.files[0];
                    if (dt) { fileRef.current.files = e.dataTransfer.files; handleFile({ target: { files: [dt] } }); }
                  }}
                  role="button" tabIndex={0} aria-label="Upload CSV file"
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
                >
                  <div style={{ fontSize: 36, marginBottom: 12 }} aria-hidden="true">📄</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--navy)', marginBottom: 6 }}>
                    Drop a CSV file here or click to browse
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                    Supports .csv files up to 10MB
                  </div>
                  <input ref={fileRef} type="file" accept=".csv" onChange={handleFile}
                    style={{ display: 'none' }} aria-label="CSV file input" />
                </div>

                <div style={{ marginTop: 20, padding: 16, background: 'var(--light-bg)', borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>Expected columns</div>
                  <div style={{ fontSize: 12, color: 'var(--text-sub)', lineHeight: 1.8 }}>
                    <strong>Required:</strong> property_address, unit_number<br />
                    <strong>For new properties:</strong> city, state, zip, property_name, landlord_email<br />
                    <strong>For tenants:</strong> email, first_name, last_name, phone<br />
                    <strong>Compliance:</strong> lease_type, lease_start, lease_end, last_renewal
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Column mapping + preview */}
          {parsed && !results && (
            <>
              <div className="card">
                <div className="card-header">
                  <div className="card-title">
                    Column Mapping
                    <span style={{ fontWeight: 400, color: 'var(--text-sub)', fontSize: 12, marginLeft: 8 }}>
                      {mappedCount} of {parsed.headers.length} columns mapped
                    </span>
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={reset}>Change file</button>
                </div>
                <div className="card-body">
                  <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 16 }}>
                    File: <strong>{file?.name}</strong> &middot; {parsed.rows.length} rows detected
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '10px 16px', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-sub)', textTransform: 'uppercase' }}>CSV Column</div>
                    <div />
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-sub)', textTransform: 'uppercase' }}>Maps To</div>
                    {parsed.headers.map(h => (
                      <div key={h} style={{ display: 'contents' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', padding: '6px 0' }}>{h}</div>
                        <div style={{ color: 'var(--text-sub)', fontSize: 16 }} aria-hidden="true">&rarr;</div>
                        <select
                          className="form-input"
                          style={{ padding: '6px 10px', fontSize: 13 }}
                          value={columnMap[h] || ''}
                          onChange={e => handleMapChange(h, e.target.value)}
                          aria-label={`Map column ${h}`}
                        >
                          <option value="">-- skip --</option>
                          {EXPECTED_COLUMNS.map(ec => (
                            <option key={ec} value={ec}
                              disabled={Object.values(columnMap).includes(ec) && columnMap[h] !== ec}
                            >{ec}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Data preview */}
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Preview (first {Math.min(10, parsed.rows.length)} rows)</div>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        {parsed.headers.filter(h => columnMap[h]).map(h => (
                          <th key={h} scope="col">
                            <div style={{ fontSize: 10, color: 'var(--text-sub)' }}>{h}</div>
                            <div style={{ fontSize: 11, color: 'var(--navy)', fontWeight: 700 }}>{columnMap[h]}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i}>
                          <td style={{ color: 'var(--text-sub)', fontSize: 12 }}>{i + 1}</td>
                          {parsed.headers.filter(h => columnMap[h]).map(h => (
                            <td key={h} style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row[h] || <span style={{ color: 'var(--text-sub)' }}>--</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Lease category breakdown */}
              {leaseColHeader && Object.keys(categoryCounts).length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <div className="card-title">Lease Category Breakdown</div>
                  </div>
                  <div className="card-body">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: optInCount > 0 ? 16 : 0 }}>
                      {Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).map(([cat, count]) => {
                        const isOptIn = OPT_IN_CATEGORIES.includes(cat);
                        return (
                          <div key={cat} style={{
                            padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                            background: isOptIn ? '#fff7ed' : 'var(--light-bg)',
                            color: isOptIn ? '#92400e' : 'var(--navy)',
                            border: `1px solid ${isOptIn ? '#fde68a' : 'var(--border)'}`,
                          }}>
                            {LEASE_LABELS[cat] || cat}: {count}
                          </div>
                        );
                      })}
                    </div>
                    {optInCount > 0 && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px' }}>
                        <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                          Opt-In Required: {optInCount} tenant{optInCount !== 1 ? 's' : ''} (Section 8, SCRIE, DRIE, Rent Controlled)
                        </div>
                        <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
                          These tenants will be imported with <strong>enrollment_type = opt_in_required</strong>. They will not be auto-enrolled
                          but can sign up manually through the portal. Contact their property manager to coordinate enrollment.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Dry run banner */}
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 18px', marginTop: 4, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, color: '#0369a1', fontSize: 13, marginBottom: 4 }}>Safe mode: Dry run by default</div>
                <div style={{ fontSize: 12, color: '#0369a1', lineHeight: 1.6 }}>
                  Clicking "Preview Import" will validate all rows and show what would be created, without writing any database records,
                  creating Stripe customers, or sending emails. You can review the results before confirming a live import.
                </div>
              </div>

              {/* Import buttons */}
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn btn-outline" onClick={reset}>Cancel</button>
                <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center', padding: 13 }}
                  onClick={handleDryRun}
                  disabled={importing || !requiredMapped}>
                  {importing
                    ? <><div className="spinner" style={{ width: 16, height: 16, marginRight: 8 }} />Running dry run...</>
                    : `Preview Import (Dry Run) — ${parsed.rows.length} rows`
                  }
                </button>
              </div>
              {!requiredMapped && (
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8 }}>
                  Please map at least property_address and unit_number to continue.
                </div>
              )}
            </>
          )}

          {/* Results */}
          {results && (
            <>
              {results.dry_run && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, color: '#0369a1', fontSize: 14 }}>Dry Run Results — No records were created</div>
                  <div style={{ fontSize: 12, color: '#0369a1', marginTop: 4 }}>
                    This is a preview of what would happen during a live import. Review the results below, then confirm to proceed.
                  </div>
                </div>
              )}

              <div className="card">
                <div className="card-header">
                  <div className="card-title">
                    {results.dry_run ? 'Preview Results (Dry Run)' : 'Import Results (Live)'}
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={reset}>Start Over</button>
                </div>
                <div className="card-body">
                  <div className="metric-grid" style={{ marginBottom: 20 }}>
                    <div className="metric-card">
                      <div className="metric-label">Properties {results.dry_run ? 'Would Create' : 'Created'}</div>
                      <div className="metric-value" style={{ color: results.dry_run ? 'var(--accent)' : 'var(--green)' }}>{results.properties_created}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Properties Existing</div>
                      <div className="metric-value">{results.properties_existing}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Units {results.dry_run ? 'Would Create' : 'Created'}</div>
                      <div className="metric-value" style={{ color: results.dry_run ? 'var(--accent)' : 'var(--teal)' }}>{results.units_created}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Tenants {results.dry_run ? 'Would Create' : 'Created'}</div>
                      <div className="metric-value" style={{ color: results.dry_run ? 'var(--accent)' : 'var(--green)' }}>{results.tenants_created}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Tenants {results.dry_run ? 'Would Skip' : 'Skipped'}</div>
                      <div className="metric-value" style={{ color: results.tenants_skipped > 0 ? 'var(--orange)' : undefined }}>
                        {results.tenants_skipped}
                      </div>
                    </div>
                    {results.opt_in_count > 0 && (
                      <div className="metric-card" style={{ borderColor: '#fde68a' }}>
                        <div className="metric-label">Opt-In Required</div>
                        <div className="metric-value" style={{ color: 'var(--orange)' }}>{results.opt_in_count}</div>
                      </div>
                    )}
                  </div>

                  {results.errors.length > 0 && (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>
                        {results.dry_run ? 'Validation Issues' : 'Errors'} ({results.errors.length})
                      </div>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr><th scope="col">Row</th><th scope="col">Reason</th></tr>
                          </thead>
                          <tbody>
                            {results.errors.slice(0, 50).map((err, i) => (
                              <tr key={i}>
                                <td style={{ fontWeight: 600 }}>{err.row}</td>
                                <td style={{ fontSize: 12, color: 'var(--red)' }}>{err.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {results.errors.length > 50 && (
                        <div style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 8 }}>
                          Showing first 50 of {results.errors.length} errors
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Live import confirmation — only after successful dry run */}
              {results.dry_run && results.tenants_created > 0 && (
                <div className="card" style={{ borderColor: '#fde68a' }}>
                  <div className="card-header">
                    <div className="card-title">Confirm Live Import</div>
                  </div>
                  <div className="card-body">
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
                      <input type="checkbox" checked={liveConfirmed} onChange={e => setLiveConfirmed(e.target.checked)}
                        style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--navy)' }} />
                      <span style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
                        I confirm this is a live import. Database records will be created, and welcome emails may be sent
                        to tenants if email is configured in production.
                      </span>
                    </label>
                    <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 13 }}
                      onClick={handleLiveImport}
                      disabled={!liveConfirmed || importing}>
                      {importing
                        ? <><div className="spinner" style={{ width: 16, height: 16, marginRight: 8 }} />Running live import...</>
                        : `Confirm Live Import — ${results.tenants_created} tenants`
                      }
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
